import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { usePageTraffic } from "@/lib/usePageTraffic";
import AppChip from "@/components/AppChip";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useChartTabs } from "@/lib/chartTabs";
import { toast } from "sonner";
import { RotateCw } from "lucide-react";
import JSZip from "jszip";
import {
  listEobFiles, getMapperGaps, getCoverage, getEobQueueHealth, getEraReadiness,
  generateEra, markEraDownloaded, deleteEobFile,
  linkPayer, linkPayee, linkPayeeNpi, bulkLinkPayers, bulkLinkPayeesEhi, bulkLinkPayeeNpis,
} from "@/lib/eobEraV2";
import { describeError } from "@/lib/api";
import {
  sm, amtOf, payerName, payeeName, checkNo, claimCount, checkDate, uploadedBy, fileName, norm,
  isDupCopy, eraStatusOf, isProcessed, bucketOf, heldGates, dateAge, createdAtMs,
  SINCE, SCOPE_LABELS, SCOPE_DAYS, mapReady, npiValid,
  isFailed, isInProgress, partiallyComplete, ageBucket, AGE_LABELS, inScopeOf, fmtCreated, fmtCheckDate, checkDateMs,
} from "@/lib/paymentsV2";
import "@/styles/paymentsV2.css";

const money = (n) => "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const kmoney = (n) => (n >= 1000 ? "$" + (n / 1000).toFixed(1) + "k" : "$" + Math.round(n || 0));
const fmtTime = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const eraName = (f) => { const d = (checkDate(f) || "").replace(/\//g, ""); return `ERA_${checkNo(f) || f.file_id.slice(0, 8)}${d ? "_" + d : ""}.txt`; };

const Ck = () => (<svg viewBox="0 0 16 16"><path d="M3 8.5l3 3 7-7" /></svg>);
const Dot = () => <span className="dot" />;

const F0 = { scope: "d7", min: "", max: "", payer: "", by: "any", gaps: false, copies: false, partial: false, review: false };

export default function CheckLedgerV2() {
  const navigate = useNavigate();
  const { openEra } = useChartTabs() || {};
  const openWs = (f) => (openEra ? openEra(f.file_id, { label: `ERA · ${checkNo(f)}` }) : navigate(`/payments-v2/${f.file_id}`));
  const [sp, setSp] = useSearchParams();

  const [files, setFiles] = useState([]);
  const { report: reportTraffic, markSearch: markSearchTraffic } = usePageTraffic("payment-posting");
  const clFirst = useRef(true);
  const [cov, setCov] = useState(null);
  const [readyMap, setReadyMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // URL-backed view state (so browser back / "Back to the ledger" restores the exact view)
  const seg = sp.get("seg") || "all";
  const q = sp.get("q") || "";
  const sortK = sp.get("sort") || "status";
  const sortDir = sp.get("dir") === "-1" ? -1 : 1;
  const flt = useMemo(() => ({
    scope: sp.get("scope") || "d7", min: sp.get("min") || "", max: sp.get("max") || "",
    payer: sp.get("payer") || "", by: sp.get("by") || "any",
    gaps: sp.get("gaps") === "1", copies: sp.get("copies") === "1", partial: sp.get("partial") === "1", review: sp.get("review") === "1",
  }), [sp]);
  const setQuery = useCallback((patch) => {
    setSp((prev) => {
      const p = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => { if (v == null || v === "" || v === "any" || v === false || (v === "1" && k === "dir")) p.delete(k); else p.set(k, String(v)); });
      return p;
    }, { replace: true });
  }, [setSp]);
  const setFlt = (next) => setQuery({ scope: next.scope === "d7" ? "" : next.scope, min: next.min, max: next.max, payer: next.payer, by: next.by === "any" ? "" : next.by, gaps: next.gaps ? "1" : "", copies: next.copies ? "1" : "", partial: next.partial ? "1" : "", review: next.review ? "1" : "" });

  // UI-only state
  const [fixOpen, setFixOpen] = useState(null);       // bulk gap row expanded: 'npi'|'ehi'|'edi'
  const [drafts, setDrafts] = useState({});           // input drafts (bulk + band)
  const [menu, setMenu] = useState(null);             // {id, x, y}
  const [confirmDel, setConfirmDel] = useState(null); // 'check:<id>' | 'r<id>:<i>'
  const [openCopies, setOpenCopies] = useState(new Set());
  const [invalidSet, setInvalidSet] = useState(new Set()); // files that returned structural_invalid on generate
  const [fpop, setFpop] = useState(null);             // filter draft or null
  const fbtnRef = useRef(null);
  const moreBtnRef = useRef(null);
  const [fpopAnchor, setFpopAnchor] = useState(null); // DOM node the filter popover anchors to
  const [toolMenu, setToolMenu] = useState(false);    // ⋯ overflow menu (narrow toolbar)
  const [sel, setSel] = useState(new Set());
  const [searchOpen, setSearchOpen] = useState(!!(sp.get("q")));
  const showGaps = true;                              // held rows always show their gap band (the old toggle folded into the "Missing a mapping" filter)
  const [scopeMenu, setScopeMenu] = useState(null);   // anchor rect for scope dropdown
  const scopeBtnRef = useRef(null);
  const searchRef = useRef(null);
  const sub = sp.get("sub") || null;                  // second-row chip (URL-backed for deep links)
  const setSub = useCallback((v) => setQuery({ sub: v || "" }), [setQuery]);
  const widened = useRef(false);                      // empty-scope auto-widen guard (once per mount)

  const qhRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true); setReadyMap({});
    const since = (SINCE[flt.scope] || SINCE.all)();
    if (!clFirst.current) markSearchTraffic({ scope: flt.scope });
    getCoverage().then(setCov).catch(() => {});
    Promise.all([listEobFiles({ since }), getMapperGaps().catch(() => ({ gaps: [] }))])
      .then(([fs]) => { setFiles(fs || []); if (clFirst.current) clFirst.current = false; reportTraffic((fs || []).length); })
      .catch((e) => toast.error(describeError(e)))
      .finally(() => setLoading(false));
  }, [flt.scope, reportTraffic, markSearchTraffic]);
  useEffect(load, [load]);

  // quiet queue heartbeat
  useEffect(() => {
    let alive = true; let t;
    const tick = async () => {
      try { const qh = await getEobQueueHealth(); if (alive) qhRef.current = qh; } catch { /* ignore */ }
      if (alive) t = setTimeout(tick, 30000);
    };
    t = setTimeout(tick, 1500);
    return () => { alive = false; clearTimeout(t); };
  }, []);

  // collapse duplicate copies under their original (API-flagged only)
  const { ledger, copyCount, copyChecks } = useMemo(() => {
    const copies = files.filter(isDupCopy);
    const byOrig = {};
    copies.forEach((c) => { const k = c.duplicate?.original_file_id; if (k) (byOrig[k] ||= []).push(c); });
    const rows = files.filter((f) => !isDupCopy(f)).map((f) => ({ f, copies: byOrig[f.file_id] || [] }));
    return { ledger: rows, copyCount: copies.length, copyChecks: rows.filter((r) => r.copies.length).length };
  }, [files]);

  // per-file readiness fan-out (drives held/ready) — verbatim getEraReadiness
  useEffect(() => {
    let alive = true;
    const targets = ledger.filter((r) => isProcessed(r.f) && eraStatusOf(r.f) === "none" && !(r.f.file_id in readyMap));
    if (!targets.length) return;
    Promise.allSettled(targets.map((r) => getEraReadiness(r.f.file_id).then((rd) => [r.f.file_id, rd])))
      .then((res) => {
        if (!alive) return;
        const add = {};
        res.forEach((x) => { if (x.status === "fulfilled" && x.value) add[x.value[0]] = x.value[1]; });
        if (Object.keys(add).length) setReadyMap((m) => ({ ...m, ...add }));
      });
    return () => { alive = false; };
  }, [ledger]); // eslint-disable-line

  const bkt = useCallback((f) => { const b = bucketOf(f, readyMap); return invalidSet.has(f.file_id) && b === "ready" ? "held" : b; }, [readyMap, invalidSet]);

  // Empty-scope fallback: if the default 7-day scope has no checks but older ones exist,
  // auto-widen to All time once so the page never opens looking blank. Manual scope wins after.
  useEffect(() => {
    if (widened.current || flt.scope === "all" || !ledger.length) return;
    if (!ledger.some((r) => inScope(r.f))) { widened.current = true; setFlt({ ...flt, scope: "all" }); }
  }); // eslint-disable-line

  // non-scope filters (amount/payer/added-by + flags) — shared by view and the older-held line
  const passFlags = useCallback((r) => {
    const f = r.f;
    if (flt.min !== "" && amtOf(f) < +flt.min) return false;
    if (flt.max !== "" && amtOf(f) > +flt.max) return false;
    if (flt.payer && !norm(payerName(f)).includes(norm(flt.payer))) return false;
    if (flt.by !== "any" && uploadedBy(f) !== flt.by) return false;
    if (flt.gaps && !mappingGap(f, readyMap, bkt)) return false;
    if (flt.copies && !r.copies.length) return false;
    if (flt.partial && !partiallyComplete(f)) return false;
    if (flt.review && !f.needs_review) return false;
    return true;
  }, [flt, readyMap, bkt]);
  const inScope = useCallback((f) => inScopeOf(f, flt.scope), [flt.scope]);
  const passF = useCallback((r) => inScope(r.f) && passFlags(r), [inScope, passFlags]);

  // second-row chip predicate (applies only within Processing / Held)
  const subPass = useCallback((f) => {
    if (!sub) return true;
    if (seg === "processing") { if (sub === "inprog") return isInProgress(f); if (sub === "failed") return isFailed(f); }
    if (seg === "held") {
      if (sub.startsWith("miss:")) { const k = sub.slice(5); return (heldGates(f, readyMap) || []).some((g) => g.kind === k); }
      if (sub.startsWith("age:")) return ageBucket(checkDate(f)) === sub.slice(4);
    }
    return true;
  }, [sub, seg, readyMap]);

  const view = useMemo(() => {
    const ord = { processing: -1, held: 0, ready: 1, generated: 2, downloaded: 3, other: 4 };
    const cmp = {
      status: (a, b) => (ord[bkt(a.f)] - ord[bkt(b.f)]) || ((dateAge(checkDate(b.f)) || 0) - (dateAge(checkDate(a.f)) || 0)),
      check: (a, b) => checkNo(a.f).localeCompare(checkNo(b.f)),
      payer: (a, b) => payerName(a.f).localeCompare(payerName(b.f)),
      payee: (a, b) => payeeName(a.f).localeCompare(payeeName(b.f)),
      date: (a, b) => (checkDateMs(checkDate(a.f)) || 0) - (checkDateMs(checkDate(b.f)) || 0),
      created: (a, b) => (createdAtMs(a.f) || 0) - (createdAtMs(b.f) || 0),
      amt: (a, b) => amtOf(a.f) - amtOf(b.f),
    }[sortK] || (() => 0);
    return ledger
      .filter((r) => (seg === "all" || bkt(r.f) === seg) && (!q || checkNo(r.f).includes(q)) && passF(r) && subPass(r.f))
      .sort((a, b) => cmp(a, b) * sortDir);
  }, [ledger, seg, q, sortK, sortDir, passF, subPass, bkt]);

  const segList = (k) => ledger.filter((r) => passF(r) && (k === "all" || bkt(r.f) === k));
  const SEGS = [["all", "All"], ["processing", "Processing"], ["held", "Held"], ["ready", "Ready"], ["generated", "ERA generated"], ["downloaded", "Downloaded"]];
  const anyFailed = ledger.some((r) => isFailed(r.f));

  // older held money hidden by the current scope (only when scope ≠ All time)
  const olderHeld = useMemo(() => {
    if (flt.scope === "all") return { n: 0, sum: 0 };
    const rows = ledger.filter((r) => bkt(r.f) === "held" && !inScope(r.f) && passFlags(r));
    return { n: rows.length, sum: rows.reduce((s, r) => s + amtOf(r.f), 0) };
  }, [ledger, flt.scope, inScope, passFlags, bkt]);

  const nAct = (flt.min !== "" || flt.max !== "" ? 1 : 0) + (flt.payer ? 1 : 0) + (flt.by !== "any" ? 1 : 0) + (flt.gaps ? 1 : 0) + (flt.copies ? 1 : 0) + (flt.partial ? 1 : 0) + (flt.review ? 1 : 0);

  // ---------- actions (each = one endpoint) ----------
  const reloadSoft = () => { setReadyMap({}); const since = (SINCE[flt.scope] || SINCE.all)(); getCoverage().then(setCov).catch(() => {}); listEobFiles({ since }).then((fs) => setFiles(fs || [])).catch(() => {}); };

  const doGenerate = async (ids) => {
    if (!ids.length) return; setBusy(true);
    let ok = 0, bad = 0, held = 0; const inv = new Set(invalidSet);
    for (const id of ids) {
      const f = files.find((x) => x.file_id === id); if (!f) continue;
      const b = bkt(f); if (b === "held") { held++; continue; } if (b !== "ready") continue;
      try {
        const r = await generateEra(id);
        if (r && r.ok !== false && r.era_text) { ok++; inv.delete(id); }
        else if (r?.error === "structural_invalid") { inv.add(id); bad++; }
        else if (r?.error === "mapping_incomplete") held++;
        else bad++;
      } catch { bad++; }
    }
    setInvalidSet(inv); setBusy(false); reloadSoft();
    toast[bad ? "warning" : "success"]([ok && `${ok} generated`, bad && `${bad} failed structural validation`, held && `${held} held, skipped`].filter(Boolean).join(" · ") || "Nothing to generate");
  };

  const doDownload = async (ids) => {
    const list = ids.map((id) => files.find((x) => x.file_id === id)).filter((f) => f && ["generated", "downloaded"].includes(eraStatusOf(f)));
    if (!list.length) return; setBusy(true);
    try {
      if (list.length === 1) {
        const f = list[0]; const r = await generateEra(f.file_id);
        if (!r?.era_text) throw new Error(r?.detail || r?.error || "Could not build the 835");
        saveBlob(new Blob([r.era_text], { type: "text/plain;charset=utf-8" }), r.filename || eraName(f));
        await markEraDownloaded(f.file_id); toast.success("835 downloaded");
      } else {
        const zip = new JSZip(); const used = {};
        for (const f of list) {
          const r = await generateEra(f.file_id); if (!r?.era_text) continue;
          let n = r.filename || eraName(f); if (used[n]) n = n.replace(/\.txt$/, "") + `_${used[n]}.txt`; used[n] = (used[n] || 0) + 1;
          zip.file(n, r.era_text); await markEraDownloaded(f.file_id);
        }
        const blob = await zip.generateAsync({ type: "blob" });
        saveBlob(blob, `ERA_${list.length}_checks.zip`); toast.success(`${list.length} 835s downloaded as ZIP`);
      }
    } catch (e) { toast.error(describeError(e)); } finally { setBusy(false); reloadSoft(); }
  };

  const doDelete = async (ids) => {
    setBusy(true);
    try { for (const id of ids) await deleteEobFile(id); toast.success(`${ids.length} deleted`); }
    catch (e) { toast.error(describeError(e)); } finally { setBusy(false); setConfirmDel(null); const s = new Set(sel); ids.forEach((i) => s.delete(i)); setSel(s); reloadSoft(); }
  };

  const saveMap = async (kind, entityName, value) => {
    setBusy(true);
    try {
      if (kind === "edi") await linkPayer(entityName, value);
      else if (kind === "ehi") await linkPayee(entityName, value);
      else if (kind === "npi") await linkPayeeNpi(entityName, value);
      toast.success("Saved to the account");
    } catch (e) { toast.error(describeError(e)); } finally { setBusy(false); reloadSoft(); }
  };

  const bulkSave = async (kind, rows) => {
    const links = rows.filter((r) => r.value).map((r) => kind === "edi" ? { payer_name: r.name, edi_payer_id: r.value } : kind === "ehi" ? { payee_name: r.name, ehi_client_id: r.value } : { payee_name: r.name, payee_npi: r.value });
    if (!links.length) return; setBusy(true);
    try {
      if (kind === "edi") await bulkLinkPayers(links); else if (kind === "ehi") await bulkLinkPayeesEhi(links); else await bulkLinkPayeeNpis(links);
      toast.success(`${links.length} saved`);
      setDrafts((d) => { const n = { ...d }; rows.forEach((r) => delete n["blk:" + kind + ":" + r.name]); return n; });
      setFixOpen(null);
    } catch (e) { toast.error(describeError(e)); } finally { setBusy(false); reloadSoft(); }
  };

  // ---------- derived counts ----------
  const vSum = view.reduce((s, r) => s + amtOf(r.f), 0);
  const readyView = view.filter((r) => bkt(r.f) === "ready");
  const genView = view.filter((r) => bkt(r.f) === "generated");

  const clearMenu = () => setMenu(null);
  // one in-row panel at a time (copies list / delete confirms); its record gets focus
  const recOf = (v) => (v?.startsWith("check:") ? v.slice(6) : (/^r(.+):\d+$/.exec(v || "") || [])[1] || null);
  const panelId = recOf(confirmDel) || [...openCopies][0] || null;
  const focusId = panelId && bkt(files.find((x) => x.file_id === panelId) || {}) !== "processing" ? panelId : null;
  const closeFocus = useCallback(() => { setConfirmDel((v) => (v === "bulk" ? v : null)); setOpenCopies(new Set()); }, []);
  const askDel = (v) => { setConfirmDel(v); const id = recOf(v); if (id) setOpenCopies((s) => (s.has(id) ? s : new Set())); };
  const toggleCopies = (id) => {
    setOpenCopies((s) => (s.has(id) ? new Set() : new Set([id])));
    setConfirmDel((v) => (v === "bulk" ? v : null));
  };
  // Esc order: ⋯ menu → Filters → open in-row panel (focus back to that check's row)
  const escRef = useRef({});
  escRef.current = { menu, fpop, focusId };
  useEffect(() => {
    const h = (e) => {
      if (e.key !== "Escape") return;
      const g = escRef.current;
      if (g.menu) { setMenu(null); return; }
      if (g.fpop) { setFpop(null); return; }
      if (g.focusId) { closeFocus(); setTimeout(() => document.querySelector(`tbody[data-rec="${g.focusId}"] > tr:first-child`)?.focus(), 0); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [closeFocus]);
  // while focused, a click on another record only closes the panel
  const onTableClickCapture = (e) => {
    if (!focusId) return;
    const rec = e.target.closest("tbody.rec");
    if (!rec || rec.dataset.rec === focusId) return;
    e.preventDefault(); e.stopPropagation(); closeFocus();
  };
  useEffect(() => { const h = () => clearMenu(); window.addEventListener("scroll", h, true); window.addEventListener("resize", h); return () => { window.removeEventListener("scroll", h, true); window.removeEventListener("resize", h); }; }, []);

  // "/" opens the check-number search when the user is not already typing somewhere
  useEffect(() => {
    const h = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target; const tag = (t.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || t.isContentEditable) return;
      e.preventDefault(); setSearchOpen(true); setTimeout(() => searchRef.current?.focus(), 30);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  return (
    <div className="ppv2" onClick={() => { if (menu) clearMenu(); }} data-testid="ppv2-ledger">
      <div className="page">
        <div className="head">
          <div className="ttl">
            <span className="lbl">Payment Posting</span>
            <div className="t1"><h1>ERA Builder</h1><AppChip app="revq" /></div>
            <span className="lede2">Every EOB revQ has read, and the 835 ERAs built from it.</span>
          </div>
          <div className="hactions">
            <button className="iconbtn" onClick={load} title="Refresh" aria-label="Refresh" data-testid="ppv2-refresh">
              <RotateCw size={16} strokeWidth={1.7} />
            </button>
          </div>
        </div>

        {loading ? <LedgerSkeleton /> : <>
        <MappingsCard cov={cov} ledger={ledger} readyMap={readyMap} bkt={bkt}
          fixOpen={fixOpen} setFixOpen={setFixOpen} drafts={drafts} setDrafts={setDrafts}
          onBulkSave={bulkSave} invalidCount={invalidSet.size}
          onFixInvalid={() => { const r = view.find((x) => invalidSet.has(x.f.file_id)); if (r) openWs(r.f); }} />

        <section className="card ledger">
          <div className="lbar">
            <div className="seg" role="tablist" aria-label="Status">
              {SEGS.map(([k, l]) => { const list = segList(k); return (
                <button key={k} role="tab" aria-selected={seg === k} className={seg === k ? "on" : ""}
                  data-testid={`ppv2-seg-${k}`} onClick={() => setQuery({ seg: k === "all" ? "" : k, sub: "" })}>
                  <span className="s1">{segDot(k, list.length, anyFailed)}{l}</span>
                  <span className="s2 tab"><b>{list.length}</b><span className="m"> · {k === "processing" ? "no amount yet" : kmoney(list.reduce((s, r) => s + amtOf(r.f), 0))}</span></span>
                </button>
              ); })}
            </div>
            <div className="sp" />
            <div className="ltg" data-testid="ppv2-toolgroup">
              <button ref={scopeBtnRef} className="btn scopebtn" aria-haspopup="menu" data-testid="ppv2-scope-btn"
                onClick={() => setScopeMenu(scopeMenu ? null : scopeBtnRef.current.getBoundingClientRect())}>
                <span className="faint">Uploaded</span> {SCOPE_LABELS[flt.scope]}
                <svg viewBox="0 0 16 16"><path d="M4 6l4 4 4-4" /></svg>
              </button>
              <label className={`csw ${searchOpen || q ? "open" : ""}`} title="Search by check number (/)"
                onClick={() => { if (!(searchOpen || q)) { setSearchOpen(true); setTimeout(() => searchRef.current?.focus(), 30); } }}>
                <svg viewBox="0 0 16 16"><circle cx="7" cy="7" r="5" /><path d="M11 11l3.5 3.5" /></svg>
                <input ref={searchRef} placeholder="Check no." value={q} aria-label="Search by check number"
                  onChange={(e) => setQuery({ q: e.target.value.trim() })}
                  onFocus={() => setSearchOpen(true)}
                  onBlur={() => { if (!q) setSearchOpen(false); }}
                  onKeyDown={(e) => { if (e.key === "Escape") { setQuery({ q: "" }); setSearchOpen(false); e.currentTarget.blur(); } }}
                  data-testid="ppv2-search" />
                {q ? <button className="csx" aria-label="Clear search" onMouseDown={(e) => { e.preventDefault(); setQuery({ q: "" }); setSearchOpen(false); }}>×</button> : null}
              </label>
              <button ref={fbtnRef} className={`btn fbtn ficon ${nAct ? "act" : ""}`} data-testid="ppv2-filters-btn"
                title="Sort & Filter" aria-label="Sort & Filter"
                onClick={() => { setFpopAnchor(fbtnRef.current); setFpop(fpop ? null : { ...flt }); }}>
                <svg viewBox="0 0 16 16"><path d="M2 4h12M4 8h8M6 12h4" /></svg>{nAct ? <span className="fcount tab">{nAct}</span> : null}
              </button>
            </div>
            <button ref={moreBtnRef} className={`morebtn ${toolMenu ? "open" : ""} ${q || nAct || flt.scope !== "d7" ? "act" : ""}`} aria-haspopup="menu" aria-label="More toolbar options" data-testid="ppv2-more-btn"
              title={[q ? `Check no. ${q}` : null, `Uploaded ${SCOPE_LABELS[flt.scope]}`, nAct ? `${nAct} filter${nAct !== 1 ? "s" : ""}` : null].filter(Boolean).join(" · ")}
              onClick={() => setToolMenu((v) => !v)}>
              <svg viewBox="0 0 20 20"><circle cx="4" cy="10" r="1.25" /><circle cx="10" cy="10" r="1.25" /><circle cx="16" cy="10" r="1.25" /></svg>
            </button>
            {toolMenu ? <ToolMenu anchor={moreBtnRef.current} q={q} setQuery={setQuery} scope={flt.scope} onScope={(k) => setFlt({ ...flt, scope: k })} nAct={nAct}
              onFilters={() => { setToolMenu(false); setFpopAnchor(moreBtnRef.current); setFpop({ ...flt }); }} onClose={() => setToolMenu(false)} /> : null}
          </div>

          {seg === "processing" || seg === "held" ? <SecondRow seg={seg} list={segList(seg)} readyMap={readyMap} sub={sub} setSub={setSub} /> : null}

          {olderHeld.n ? <div className="older" data-testid="ppv2-older-held"><span className="rdot" />
            <b>{olderHeld.n} held check{olderHeld.n !== 1 ? "s" : ""} were uploaded more than 7 days ago</b>
            <span className="faint tab">· {money(olderHeld.sum)}</span>
            <button className="lnk" onClick={() => { setFlt({ ...flt, scope: "all" }); setQuery({ seg: "held" }); }}>Show them</button></div> : null}

          <div className="cap" data-testid="ppv2-caption">
            <b className="tab">{view.length} check{view.length !== 1 ? "s" : ""}</b>
            <span className="faint tab">{money(vSum)}</span>
            {copyCount && !flt.copies ? <><span className="faint">·</span>
              <button className="dup" onClick={() => setFlt({ ...flt, copies: true })}><Dot />{copyCount} duplicate copies across {copyChecks} checks</button></> : null}
            {activeChips(flt).map((c) => (<span key={c.k} className="chip">{c.label}<button aria-label="Remove" onClick={() => setFlt({ ...flt, ...c.clear })}>×</button></span>))}
            {nAct > 1 ? <button className="txt" onClick={() => setFlt({ ...F0 })}>Clear all</button> : null}
            <span className="sp" />
            {genView.length ? <button className="btn sm" disabled={busy} onClick={() => doDownload(genView.map((r) => r.f.file_id))} data-testid="ppv2-dl-all">
              <svg viewBox="0 0 16 16"><path d="M8 2v8M4.5 7L8 10.5 11.5 7M2.5 13.5h11" /></svg>Download {genView.length > 1 ? genView.length + " as ZIP" : "835"}</button> : null}
            {readyView.length ? <button className="btn pri sm" disabled={busy} onClick={() => doGenerate(readyView.map((r) => r.f.file_id))} data-testid="ppv2-gen-all">Generate all ready · {readyView.length}</button> : null}
          </div>

          <table className={focusId ? "focus" : ""} onClickCapture={onTableClickCapture} data-testid="ppv2-table">
            <Thead sortK={sortK} sortDir={sortDir} onSort={(k) => setQuery(k === sortK ? { dir: sortDir > 0 ? -1 : 1 } : { sort: k, dir: (k === "amt" || k === "claims") ? -1 : 1 })}
              allSel={view.length > 0 && view.every((r) => sel.has(r.f.file_id))}
              onAll={(chk) => { const s = new Set(sel); view.forEach((r) => chk ? s.add(r.f.file_id) : s.delete(r.f.file_id)); setSel(s); }} />
            {view.length ? view.map((r) => (
                <Row key={r.f.file_id} r={r} state={bkt(r.f)} readyMap={readyMap}
                  sel={sel.has(r.f.file_id)} onSel={(chk) => { const s = new Set(sel); chk ? s.add(r.f.file_id) : s.delete(r.f.file_id); setSel(s); }}
                  onOpen={() => openWs(r.f)}
                  onMenu={(x, y) => setMenu({ id: r.f.file_id, x, y })}
                  drafts={drafts} setDrafts={setDrafts} onSaveMap={saveMap} busy={busy}
                  onGen={() => doGenerate([r.f.file_id])} onDl={() => doDownload([r.f.file_id])} onMark={async () => { setBusy(true); try { await markEraDownloaded(r.f.file_id); } finally { setBusy(false); reloadSoft(); } }}
                  invalid={invalidSet.has(r.f.file_id)} openCopies={openCopies} onToggleCopies={() => toggleCopies(r.f.file_id)}
                  confirmDel={confirmDel} setConfirmDel={askDel} onDelete={doDelete} focused={focusId === r.f.file_id} showGaps={showGaps} />
              )) : <tbody><tr><td colSpan={10} className="empty">No checks match. <button className="lnk" onClick={() => { setQuery({ seg: "", q: "" }); setFlt({ ...F0 }); }}>Show all checks</button></td></tr></tbody>}
          </table>
        </section>
        </>}
      </div>

      {/* selection bar */}
      <SelBar sel={sel} files={files} bkt={bkt} busy={busy}
        onGen={() => { doGenerate([...sel].filter((id) => bkt(files.find((f) => f.file_id === id)) === "ready")); }}
        onDl={() => { doDownload([...sel]); }} onDel={() => setConfirmDel(`bulk`)} onClear={() => setSel(new Set())} />
      {confirmDel === "bulk" ? <ConfirmBar n={sel.size} onYes={() => doDelete([...sel])} onNo={() => setConfirmDel(null)} /> : null}

      {menu ? <RowMenu menu={menu} onClose={clearMenu} navigate={navigate}
        f={files.find((x) => x.file_id === menu.id)} state={bkt(files.find((x) => x.file_id === menu.id))}
        onMark={async () => { clearMenu(); setBusy(true); try { await markEraDownloaded(menu.id); } finally { setBusy(false); reloadSoft(); } }}
        onDl={() => { clearMenu(); doDownload([menu.id]); }}
        onDelete={() => { askDel(`check:${menu.id}`); clearMenu(); }} /> : null}

      {scopeMenu ? <ScopeMenu rect={scopeMenu} current={flt.scope} onClose={() => setScopeMenu(null)}
        onPick={(k) => { setFlt({ ...flt, scope: k }); setScopeMenu(null); }} /> : null}

      {fpop ? <FilterPop draft={fpop} setDraft={setFpop} anchor={fpopAnchor || fbtnRef.current} ledger={ledger} bkt={bkt} readyMap={readyMap}
        onApply={(d) => { setFlt(d); setFpop(null); }} onClose={() => setFpop(null)} /> : null}
    </div>
  );
}

function ToolMenu({ anchor, q, setQuery, scope, onScope, nAct, onFilters, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => { if (anchor) { const b = anchor.getBoundingClientRect(); const w = 240; setPos({ top: b.bottom + 6, left: Math.max(8, Math.min(b.right - w, window.innerWidth - w - 8)) }); } }, [anchor]);
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target) && e.target !== anchor && !anchor?.contains(e.target)) onClose(); }; setTimeout(() => document.addEventListener("mousedown", h), 0); const w = () => onClose(); window.addEventListener("resize", w); return () => { document.removeEventListener("mousedown", h); window.removeEventListener("resize", w); }; }, [anchor, onClose]);
  return (
    <div ref={ref} className="ppv2-toolmenu ppv2" role="menu" data-testid="ppv2-toolmenu" style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? "visible" : "hidden" }}>
      <div className="tmlbl">Search</div>
      <input className="inp tmsearch" placeholder="Check no." defaultValue={q} autoFocus onChange={(e) => setQuery({ q: e.target.value.trim() })} data-testid="ppv2-toolmenu-search" />
      <div className="tmhr" />
      <div className="tmlbl">Uploaded</div>
      {Object.entries(SCOPE_LABELS).map(([k, l]) => (
        <button key={k} role="menuitemradio" aria-checked={scope === k} className="tmitem" onClick={() => onScope(k)} data-testid={`ppv2-toolmenu-scope-${k}`}>
          <span>{l}</span>{scope === k ? <span aria-hidden="true">✓</span> : null}
        </button>
      ))}
      <div className="tmhr" />
      <button className="tmitem" onClick={onFilters} data-testid="ppv2-toolmenu-filters"><span>Filters…</span>{nAct ? <span className="tab faint">{nAct}</span> : null}</button>
    </div>
  );
}

function ScopeMenu({ rect, current, onClose, onPick }) {
  const ref = useRef(null);
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); }; setTimeout(() => document.addEventListener("mousedown", h), 0); const w = () => onClose(); window.addEventListener("resize", w); window.addEventListener("scroll", w, true); return () => { document.removeEventListener("mousedown", h); window.removeEventListener("resize", w); window.removeEventListener("scroll", w, true); }; }, [onClose]);
  const w = 180;
  return (
    <div ref={ref} className="ppv2-rowmenu" role="menu" data-testid="ppv2-scope-menu"
      style={{ left: Math.max(8, Math.min(rect.left, window.innerWidth - w - 8)), top: rect.bottom + 4, minWidth: w }}>
      {Object.entries(SCOPE_LABELS).map(([k, l]) => (
        <button key={k} role="menuitemradio" aria-checked={current === k} onClick={() => onPick(k)}
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {l}{current === k ? <span aria-hidden="true">✓</span> : null}</button>
      ))}
    </div>
  );
}

// ---------- pieces ----------
function LedgerSkeleton() {
  const S = (w, h, style) => <span className="sk" style={{ width: w, height: h, ...style }} />;
  return (
    <div data-testid="ppv2-skeleton">
      <section className="card needs" style={{ marginBottom: 14 }}>
        <div className="nh">{S(120, 12)}{S(150, 14)}{S(150, 14)}{S(110, 14)}</div>
      </section>
      <section className="card ledger">
        <div className="lbar">{S(430, 40, { borderRadius: 11 })}<div className="sp" />{S(230, 34, { borderRadius: 10 })}{S(96, 34, { borderRadius: 10 })}</div>
        <div className="cap">{S(110, 16)}{S(80, 14)}<div className="sp" />{S(150, 28, { borderRadius: 8 })}</div>
        <table><tbody>
          {Array.from({ length: 7 }).map((_, i) => (
            <tr key={i}>
              <td className="ck">{S(16, 16, { borderRadius: 4 })}</td>
              <td>{S("78%", 13)}{S("52%", 11, { marginTop: 5 })}</td>
              <td>{S(64, 13)}</td>
              <td>{S("80%", 13)}{S("50%", 11, { marginTop: 5 })}</td>
              <td className="hn">{S("78%", 13)}</td>
              <td className="r hn">{S(24, 13, { marginLeft: "auto" })}</td>
              <td className="hn">{S(70, 13)}</td>
              <td className="r">{S(72, 14, { marginLeft: "auto" })}</td>
              <td /><td />
            </tr>
          ))}
        </tbody></table>
      </section>
    </div>
  );
}
function segDot(k, count, anyFailed) {
  if (k === "all" || !count) return null;
  if (k === "processing") return anyFailed ? <span className="rdot" /> : null;
  const cls = { held: "dot", ready: "pdot", generated: "gdot", downloaded: "xdot" }[k];
  return cls ? <span className={cls} /> : null;
}

function SecondRow({ seg, list, readyMap, sub, setSub }) {
  const toggle = (v) => setSub(sub === v ? null : v);
  const Chip = ({ v, label, count }) => count
    ? <button className={`subchip ${sub === v ? "on" : ""}`} onClick={() => toggle(v)} data-testid={`ppv2-sub-${v}`}>{label}<span className="tab">{count}</span></button>
    : <span className="subchip zero" data-testid={`ppv2-sub-${v}`}>{label}<span className="tab">0</span></span>;
  if (seg === "processing") {
    const ip = list.filter((r) => isInProgress(r.f)).length, fa = list.filter((r) => isFailed(r.f)).length;
    return <div className="subrow" data-testid="ppv2-subrow"><div className="sgrp"><Chip v="inprog" label="In progress" count={ip} /><Chip v="failed" label="Failed" count={fa} /></div></div>;
  }
  const miss = (k) => list.filter((r) => (heldGates(r.f, readyMap) || []).some((g) => g.kind === k)).length;
  const age = (b) => list.filter((r) => ageBucket(checkDate(r.f)) === b).length;
  return (
    <div className="subrow" data-testid="ppv2-subrow">
      <div className="sgrp"><span className="slab">Missing:</span>
        <Chip v="miss:payer_id" label="EDI Payer ID" count={miss("payer_id")} />
        <Chip v="miss:ehi_client_id" label="EHI Client ID" count={miss("ehi_client_id")} />
        <Chip v="miss:payee_npi" label="Payee NPI" count={miss("payee_npi")} /></div>
      <div className="sdiv" />
      <div className="sgrp"><span className="slab">Age of check:</span>
        {Object.entries(AGE_LABELS).map(([b, l]) => <Chip key={b} v={`age:${b}`} label={l} count={age(b)} />)}</div>
    </div>
  );
}

function mappingGap(f, readyMap, bkt) {
  if (bkt(f) === "processing") return false;            // unread
  if (!payerName(f) || !payeeName(f)) return false;     // unmatched payer/payee
  try { return (heldGates(f, readyMap) || []).length > 0; } catch { return false; }
}
function activeChips(flt) {
  const c = [];
  if (flt.gaps) c.push({ k: "gaps", label: "Missing a mapping", clear: { gaps: false } });
  if (flt.min !== "" || flt.max !== "") c.push({ k: "amt", label: `${flt.min !== "" ? money(+flt.min) : "$0"} – ${flt.max !== "" ? money(+flt.max) : "any"}`, clear: { min: "", max: "" } });
  if (flt.payer) c.push({ k: "payer", label: `Payer: ${flt.payer}`, clear: { payer: "" } });
  if (flt.by !== "any") c.push({ k: "by", label: `Added by ${flt.by.split("@")[0]}`, clear: { by: "any" } });
  if (flt.copies) c.push({ k: "copies", label: "Has duplicate copies", clear: { copies: false } });
  if (flt.partial) c.push({ k: "partial", label: "Partially complete", clear: { partial: false } });
  if (flt.review) c.push({ k: "review", label: "Needs review", clear: { review: false } });
  return c;
}

function Thead({ sortK, sortDir, onSort, allSel, onAll }) {
  const COLS = [["check", "Check"], ["status", "Status"], ["payer", "Payer"], ["payee", "Payee", "", "hn"], ["date", "Check date", "", "hn"], ["created", "Added", "", "hn"], ["amt", "Amount", "r"], [null, "", "r"], [null, "", "r"]];
  return (
    <thead><tr>
      <th className="ck"><input type="checkbox" className="cb" checked={allSel} onChange={(e) => onAll(e.target.checked)} aria-label="Select all in view" /></th>
      {COLS.map(([k, l, r, h], i) => (
        <th key={i} className={`${r || ""} ${h || ""} ${k === sortK ? "sorted" : ""}`}>
          {k ? <button onClick={() => onSort(k)}>{l}{k === sortK ? <span className="ar">{sortDir > 0 ? "▲" : "▼"}</span> : null}</button>
            : <button tabIndex={-1} style={{ cursor: "default" }}>{l}</button>}
        </th>
      ))}
    </tr></thead>
  );
}

function IdChip({ label, value, full }) {
  const [copied, setCopied] = useState(false);
  const tRef = useRef(null);
  useEffect(() => () => clearTimeout(tRef.current), []);
  const doCopy = (e) => {
    e.stopPropagation();
    try { if (navigator.clipboard) navigator.clipboard.writeText(String(value)).catch(() => {}); } catch (err) { /* silent */ }
    setCopied(true);
    clearTimeout(tRef.current);
    tRef.current = setTimeout(() => setCopied(false), 1400);
  };
  return (
    <button
      type="button"
      className={`idc${copied ? " copied" : ""}`}
      aria-label={`Copy ${full} ${value}`}
      title={`Copy ${full}`}
      onClick={doCopy}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); doCopy(e); } }}
      data-testid={`ppv2-idchip-${label.toLowerCase()}-${value}`}
    >
      <svg className="ok" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3 3 7-7" /></svg>
      <span className="k">{label}</span>
      <span className="v tab">{value}</span>
      <svg className="cp" viewBox="0 0 16 16" aria-hidden="true"><rect x="5.5" y="5.5" width="8" height="8" rx="1.5" /><path d="M10.5 3.5v-.5a1.5 1.5 0 0 0-1.5-1.5H4A1.5 1.5 0 0 0 2.5 3v5A1.5 1.5 0 0 0 4 9.5h.5" /></svg>
      <span className="cpd" aria-hidden="true">Copied</span>
      <span className="idc-live" aria-live="polite">{copied ? "Copied" : ""}</span>
    </button>
  );
}

function CreatedCell({ f }) {
  const c = fmtCreated(f);
  return <td className="two hn" data-testid={`ppv2-created-${f.file_id.slice(0, 8)}`}>{c ? <><b className="tab" style={{ fontWeight: 400 }}>{c.date}</b><span className="tab">{c.time}</span></> : null}</td>;
}

function IdNotMapped({ full }) {
  return <span className="idm"><span className="dot" />{full} not mapped</span>;
}

function Row({ r, state, readyMap, sel, onSel, onOpen, onMenu, drafts, setDrafts, onSaveMap, busy, onGen, onDl, onMark, invalid, openCopies, onToggleCopies, confirmDel, setConfirmDel, onDelete, focused, showGaps }) {
  const f = r.f;
  const rd = readyMap[f.file_id];
  const edi = rd?.edi_payer_id || ""; const ehi = rd?.ehi_client_id || ""; const npi = rd?.payee_npi || "";
  const gates = state === "held" ? heldGates(f, readyMap) : null;
  const age = dateAge(checkDate(f));
  const cc = claimCount(f);

  // Processing / Failed rows — no check no, no amount, no verb, no Open (spec §7)
  if (state === "processing") {
    const failed = isFailed(f);
    return (
      <tbody className={`rec unread ${failed ? "recfail" : ""}`} data-rec={f.file_id} data-testid={`ppv2-row-proc-${f.file_id.slice(0, 8)}`}>
        <tr>
          <td className="ck"><input type="checkbox" className="cb" checked={sel} onChange={(e) => onSel(e.target.checked)} aria-label={`Select ${fileName(f)}`} onClick={(e) => e.stopPropagation()} /></td>
          <td className="two"><b className="faint" style={{ fontWeight: 600 }}>No check no. yet</b><span>{fileName(f)}</span></td>
          <td><div className="st"><b className={failed ? "rd" : ""}>{failed ? <span className="rdot" /> : null}{failed ? "Failed" : "In progress"}</b>{failed ? null : <span className="working" aria-label="revQ is reading this file" />}</div></td>
          <td className="two"><span className="faint">{failed ? "Couldn’t be read" : "Reading the remittance…"}</span></td>
          <td className="hn" />
          <td className="hn" />
          <CreatedCell f={f} />
          <td className="r" /><td className="acts" />
          <td className="more"><button className="ib2" aria-haspopup="menu" aria-label={`More for ${fileName(f)}`}
            onClick={(e) => { e.stopPropagation(); const b = e.currentTarget.getBoundingClientRect(); onMenu(b.right - 210, b.bottom + 4); }}>
            <svg viewBox="0 0 16 16"><circle cx="3.5" cy="8" r="1.2" /><circle cx="8" cy="8" r="1.2" /><circle cx="12.5" cy="8" r="1.2" /></svg></button></td>
        </tr>
        {failed ? <tr className="band"><td className="ck" /><td colSpan={9}><div className="bwrap"><div className="bline bfail">
          <span className="bwhy"><span className="rdot" />Couldn’t read this file</span><span className="bnote">{f.error || "Extraction failed"}</span></div></div></td></tr> : null}
      </tbody>
    );
  }

  const stw = {
    held: ["Held", ""],
    ready: ["Ready", ""],
    generated: ["ERA generated", "835 built" + (f.era_generated_at ? " " + fmtTime(f.era_generated_at) : "")],
    downloaded: ["Downloaded", f.era_downloaded_at ? "at " + fmtTime(f.era_downloaded_at) : ""],
  }[state] || [statusWord(f), ""];
  const warn = state === "held";
  const dot = { ready: "pdot", generated: "gdot", downloaded: "xdot" }[state];
  const band = bandFor();
  return (
    <tbody className={`rec ${band ? "hasband" : ""} ${sel ? "sel" : ""} ${warn ? "recwarn" : ""} ${state === "downloaded" ? "done" : ""} ${focused ? "focused" : ""}`} data-rec={f.file_id} data-focused={focused ? "true" : undefined} data-testid={`ppv2-row-${checkNo(f)}`}>
      <tr onClick={(e) => { if (!e.target.closest("button,input,.bwrap")) onOpen(); }} tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}>
        <td className="ck"><input type="checkbox" className="cb" checked={sel} onChange={(e) => onSel(e.target.checked)} aria-label={`Select ${checkNo(f)}`} onClick={(e) => e.stopPropagation()} /></td>
        <td className="two"><b className="tab">{checkNo(f)}</b><span>{fileName(f)}</span><span className="ph2"><b className={warn ? "am" : ""}>{stw[0]}</b> · {payerName(f)} <span className="ph2amt">· {money(amtOf(f))}</span></span></td>
        <td><div className="st"><b className={warn ? "am" : ""}>{warn ? <Dot /> : (dot ? <span className={dot} /> : null)}{stw[0]}</b>{stw[1] ? <span>{stw[1]}</span> : null}</div></td>
        <td className="two"><b>{payerName(f)}</b><span className="ids wide">{edi ? <IdChip label="EDI" value={edi} full="EDI Payer ID" /> : (rd ? <IdNotMapped full="EDI Payer ID" /> : null)}</span><span className="narrow">→ {payeeName(f)}</span><span className="ids narrow">{ehi ? <IdChip label="EHI" value={ehi} full="EHI Client ID" /> : (rd ? <IdNotMapped full="EHI Client ID" /> : null)}</span></td>
        <td className="two hn"><b>{payeeName(f)}</b><span className="ids">{ehi ? <IdChip label="EHI" value={ehi} full="EHI Client ID" /> : (rd ? <IdNotMapped full="EHI Client ID" /> : null)}{npi ? <IdChip label="NPI" value={npi} full="Payee NPI" /> : (rd ? <IdNotMapped full="Payee NPI" /> : null)}</span></td>
        <td className="two hn"><b className="tab" style={{ fontWeight: 400 }}>{fmtCheckDate(checkDate(f))}</b>{age != null ? <span className={`tab ${state === "held" ? "am" : ""}`}>{age}d ago</span> : null}</td>
        <CreatedCell f={f} />
        <td className="r"><span className="amt tab">{money(amtOf(f))}{cc != null ? <span className="subn">{cc} claim{cc !== 1 ? "s" : ""}</span> : null}</span></td>
        <td className="acts"><div className="actw">
          {state === "ready" ? <button className="btn sm" disabled={busy} onClick={onGen} data-testid={`ppv2-gen-${checkNo(f)}`}>Generate</button> : null}
          <button className="btn sm opn" onClick={onOpen} aria-label={`Open ERA workspace for check ${checkNo(f)}`} data-testid={`ppv2-open-${checkNo(f)}`}><span className="t">Open</span><svg viewBox="0 0 16 16"><path d="M3 8h10M9 4l4 4-4 4" /></svg></button>
        </div></td>
        <td className="more"><button className="ib2" aria-haspopup="menu" aria-label={`More for check ${checkNo(f)}`}
          onClick={(e) => { e.stopPropagation(); const b = e.currentTarget.getBoundingClientRect(); onMenu(b.right - 210, b.bottom + 4); }}>
          <svg viewBox="0 0 16 16"><circle cx="3.5" cy="8" r="1.2" /><circle cx="8" cy="8" r="1.2" /><circle cx="12.5" cy="8" r="1.2" /></svg></button></td>
      </tr>
      {band}
    </tbody>
  );

  function bandFor() {
    const parts = [];
    if (showGaps && state === "held" && gates && gates.length) {
      gates.forEach((g) => {
        const key = `b:${g.kind}:${f.file_id}`; const v = drafts[key] || "";
        const isNpi = g.kind === "payee_npi";
        const ok = isNpi ? npiValid(v) : v.length >= 3; const bad = isNpi && v.length === 10 && !ok;
        const entity = g.entity || (g.kind === "payer_id" ? payerName(f) : payeeName(f));
        const short = g.kind === "payer_id" ? "edi" : g.kind === "ehi_client_id" ? "ehi" : "npi";
        parts.push(
          <div className="bline" key={g.kind}>
            <span className="bwhy"><Dot />{g.label} not mapped</span>
            <input className={`inp bin ${bad ? "bad" : ""}`} placeholder={g.label} value={v} inputMode={isNpi ? "numeric" : undefined} maxLength={isNpi ? 10 : undefined}
              onClick={(e) => e.stopPropagation()} onChange={(e) => { let x = e.target.value; if (isNpi) x = x.replace(/\D/g, "").slice(0, 10); setDrafts((d) => ({ ...d, [key]: x.trim() })); }} />
            <button className="btn pri sm" disabled={!ok || busy} onClick={(e) => { e.stopPropagation(); onSaveMap(short, entity, v); }}>Save</button>
            {bad ? <span className="bnote am">Fails the NPI check digit</span> : null}
          </div>
        );
      });
    }
    if (showGaps && state === "held" && invalid) {
      parts.push(<div className="bline" key="inv"><span className="bwhy"><Dot />835 failed structural validation</span>
        <span className="bnote">No error detail returned</span><span className="sp" />
        <button className="btn sm" onClick={(e) => { e.stopPropagation(); onOpen(); }}>Fix in ERA workspace →</button>
        <button className="btn sm" disabled={busy} onClick={(e) => { e.stopPropagation(); onGen(); }}>Generate again</button></div>);
    }
    if (state === "generated") {
      parts.push(<div className="bline" key="gen"><span className="bera"><svg viewBox="0 0 16 16"><path d="M4 1.5h6l3 3v10H4z" /></svg>{eraName(f)}</span>
        <span className="bnote">Built {fmtTime(f.era_generated_at)} · balanced · structure valid · not downloaded yet</span><span className="sp" />
        <button className="btn pri sm" disabled={busy} onClick={(e) => { e.stopPropagation(); onDl(); }}><svg viewBox="0 0 16 16"><path d="M8 2v8M4.5 7L8 10.5 11.5 7M2.5 13.5h11" /></svg>Download 835</button></div>);
    }
    if (showGaps && partiallyComplete(f)) {
      const s = sm(f); const cov = Number(s.coverage_pct); const missed = Number(s.missing_claims) || 0;
      parts.push(<div className="bline" key="partial"><span className="bwhy"><Dot />Partially complete</span>
        <span className="bnote tab">{[missed ? `${missed} claim${missed !== 1 ? "s" : ""} not captured` : null, !isNaN(cov) ? `${cov}% coverage` : null, s.clean_export_blocked ? "clean export blocked" : null].filter(Boolean).join(" · ")}</span></div>);
    }
    if (showGaps && f.needs_review) {
      const reasons = Array.isArray(f.needs_review_reasons) ? f.needs_review_reasons : [];
      parts.push(<div className="bline" key="review"><span className="bwhy"><Dot />Needs review</span>
        {reasons.length ? <span className="bnote">{reasons.join(" · ")}</span> : null}</div>);
    }
    if (r.copies.length) {
      const open = openCopies.has(f.file_id);
      parts.push(<div className="bline copies" key="cop"><button className="bdisc" aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); onToggleCopies(); }} data-testid={`ppv2-copies-${checkNo(f)}`}>
        <Dot />{r.copies.length} duplicate cop{r.copies.length > 1 ? "ies" : "y"}<span className="bnote">same check, same amount · not counted</span>
        <svg className={`caret ${open ? "up" : ""}`} viewBox="0 0 16 16"><path d="M4 6l4 4 4-4" /></svg></button></div>);
      if (open) r.copies.forEach((c, i) => parts.push(
        <div className="bline bsub" key={"cop" + i}><span className="cn">Copy {i + 2}</span>
          <span className="bnote">{fileName(c)} · uploaded {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</span><span className="sp" />
          {confirmDel === `r${f.file_id}:${i}` ? <span className="confirm">Delete this copy? <button className="btn pri sm" onClick={(e) => { e.stopPropagation(); onDelete([c.file_id]); }}>Delete</button><button className="txt" onClick={(e) => { e.stopPropagation(); setConfirmDel(null); }}>Keep</button></span>
            : <button className="txt" onClick={(e) => { e.stopPropagation(); setConfirmDel(`r${f.file_id}:${i}`); }}>Delete copy</button>}</div>
      ));
    }
    if (confirmDel === `check:${f.file_id}`) parts.push(<div className="bline" key="del"><span className="confirm">Delete this check and its {claimCount(f) ?? ""} claims from revQ? <button className="btn pri sm" onClick={(e) => { e.stopPropagation(); onDelete([f.file_id]); }}>Delete check</button><button className="txt" onClick={(e) => { e.stopPropagation(); setConfirmDel(null); }}>Keep</button></span></div>);
    return parts.length ? <tr className="band"><td className="ck" /><td colSpan={9}><div className="bwrap">{parts}</div></td></tr> : null;
  }
}

function statusWord(f) {
  const s = String(f.status || "").toLowerCase();
  if (s === "failed" || s === "error") return "Failed";
  if (s === "queued") return "Queued";
  if (s === "processing" || s === "in_progress" || s === "pending") return "Processing";
  if (f.needs_review) return "Needs review";
  return "Processed";
}

function MappingsCard({ cov, ledger, readyMap, bkt, fixOpen, setFixOpen, drafts, setDrafts, onBulkSave, invalidCount, onFixInvalid }) {
  if (!cov) return null;
  const ehiTot = cov.total_payees ?? 0, ehiAl = cov.aligned ?? 0;
  const ediTot = cov.payer?.total_payers ?? 0, ediAl = cov.payer?.aligned ?? 0;
  const npiTot = cov.payee_npi?.total_payees ?? 0, npiAl = cov.payee_npi?.resolved ?? 0;
  const heldCountFor = (kind, name) => ledger.filter((r) => bkt(r.f) === "held" && (kind === "edi" ? payerName(r.f) === name : payeeName(r.f) === name)).length;

  const rows = [];
  const npiPending = cov.payee_npi?.pending_payees || [];
  const ehiPending = cov.pending_payees || [];
  const ediPending = cov.payer?.pending_payers || [];
  if (npiPending.length) rows.push({ k: "npi", title: <><b>{npiPending.length} payee{npiPending.length > 1 ? "s" : ""} need an NPI</b></>, verb: `Add ${npiPending.length} NPI${npiPending.length > 1 ? "s" : ""}`, items: npiPending, ph: "10-digit NPI", valid: npiValid, bad: "Fails the NPI check digit" });
  if (ehiPending.length) rows.push({ k: "ehi", title: <><b>{ehiPending.length} payees need an EHI client ID</b></>, verb: "Add IDs", items: ehiPending, ph: "EHI client ID", valid: (v) => /^\d{3,}$/.test(v), bad: "Digits only" });
  if (ediPending.length) rows.push({ k: "edi", title: <><b>{ediPending.length} payers need an EDI payer ID</b></>, verb: "Add IDs", items: ediPending, ph: "EDI payer ID", valid: (v) => /^[A-Z0-9]{5,}$/i.test(v), bad: "Letters and digits" });

  const gKind = { ehi: "ehi_client_id", edi: "payer_id", npi: "payee_npi" };
  const heldList = (k) => ledger.filter((r) => bkt(r.f) === "held" && (heldGates(r.f, readyMap) || []).some((g) => g.kind === gKind[k]));
  const byKind = Object.fromEntries(rows.map((r) => [r.k, r]));
  const ratios = [
    { k: "ehi", label: "Payee → EHI client ID", have: ehiAl, total: ehiTot, pending: ehiPending.length },
    { k: "edi", label: "Payer → EDI payer ID", have: ediAl, total: ediTot, pending: ediPending.length },
    { k: "npi", label: "Payee → NPI", have: npiAl, total: npiTot, pending: npiPending.length },
  ];
  const openRow = fixOpen ? byKind[fixOpen] : null;

  return (
    <section className="card needs" data-testid="ppv2-mappings">
      <div className="nh"><span className="lbl">Account mappings</span>
        <div className="mgs">
          {ratios.map((rt) => (
            <span className="mg" key={rt.k}>
              <span className="ml">{rt.label}</span><b className="tab">{rt.have} / {rt.total}</b>
              {rt.pending === 0
                ? <span className="okk"><Ck />All aligned</span>
                : <span className="miss"><Dot /><span className="am tab">{rt.pending} missing</span>
                    <button className="btn xs" aria-expanded={fixOpen === rt.k} onClick={() => setFixOpen(fixOpen === rt.k ? null : rt.k)} data-testid={`ppv2-mapadd-${rt.k}`}>{fixOpen === rt.k ? "Close" : "Add"}</button></span>}
            </span>
          ))}
        </div>
      </div>
      {openRow ? (() => {
        const r = openRow;
        const held = heldList(r.k); const amt = held.reduce((s, x) => s + amtOf(x.f), 0);
        const draftRows = r.items.map((name) => ({ name, value: drafts["blk:" + r.k + ":" + name] || "" }));
        const good = draftRows.filter((x) => r.valid(x.value)).length;
        const anyBad = draftRows.some((x) => x.value.length && !r.valid(x.value) && (r.k !== "npi" || x.value.length === 10));
        return (
          <div className="nfix open" data-testid={`ppv2-mapfix-${r.k}`}>
            <div className="nfh"><Dot /><span className="t">{r.title}</span><span className="faint tab">· holding {held.length} check{held.length !== 1 ? "s" : ""} · {money(amt)}</span></div>
            <div className="in">
              {r.items.map((name) => {
                const key = "blk:" + r.k + ":" + name; const v = drafts[key] || ""; const ok = r.valid(v);
                const isBad = v.length && !ok && (r.k !== "npi" || v.length === 10);
                const held1 = heldCountFor(r.k, name);
                return (
                  <div className="frow" key={name}>
                    <span className="pn">{name}</span>
                    <span className="faint tab">{held1 ? `${held1} held` : ""}</span>
                    <input className={`inp ${isBad ? "bad" : ""}`} placeholder={r.ph} value={v} inputMode={r.k === "npi" ? "numeric" : undefined} maxLength={r.k === "npi" ? 10 : undefined}
                      onChange={(e) => { let x = e.target.value; if (r.k === "npi") x = x.replace(/\D/g, "").slice(0, 10); setDrafts((d) => ({ ...d, [key]: x.trim() })); }} />
                    <span className={`vm ${isBad ? "bad" : ok ? "ok" : ""}`}>{isBad ? r.bad : ok ? (held1 ? `Releases ${held1}` : "Ready to save") : "Blank stays held"}</span>
                  </div>
                );
              })}
              <div className="ffoot"><p>Saved to the account — every remittance from these {r.k === "edi" ? "payers" : "payees"}, now and future, uses it.</p>
                <button className="btn pri" disabled={!good || anyBad} onClick={() => onBulkSave(r.k, draftRows)}>Save {good || ""}</button></div>
            </div>
          </div>
        );
      })() : null}
      {invalidCount ? <div className="nrow"><div className="nrh"><Dot /><span className="t"><b>{invalidCount} 835 failed structural validation</b> <span>· fix the lines in the ERA workspace, then generate again</span></span>
        <button className="btn sm" onClick={onFixInvalid}>Fix in ERA workspace →</button></div></div> : null}
    </section>
  );
}

function SelBar({ sel, files, bkt, busy, onGen, onDl, onDel, onClear }) {
  const list = files.filter((f) => sel.has(f.file_id));
  const g = list.filter((f) => bkt(f) === "ready").length;
  const d = list.filter((f) => ["generated", "downloaded"].includes(eraStatusOf(f))).length;
  const h = list.filter((f) => bkt(f) === "held").length;
  return (
    <div className={`ppv2-selbar ${list.length ? "show" : ""}`} role="toolbar" aria-label="Selection" data-testid="ppv2-selbar">
      <span className="n tab">{list.length} selected<span>{money(list.reduce((s, f) => s + amtOf(f), 0))}</span></span>
      <button className={`sb ${g ? "main" : ""}`} disabled={!g || busy} onClick={onGen}>Generate {g}</button>
      <button className="sb" disabled={!d || busy} onClick={onDl}>Download {d}</button>
      {h ? <span style={{ fontSize: 12, opacity: .7, padding: "0 6px" }}>{h} held · skipped</span> : null}
      <button className="sb" disabled={busy} onClick={onDel}>Delete…</button>
      <button className="x" aria-label="Clear selection" onClick={onClear}>✕</button>
    </div>
  );
}
function ConfirmBar({ n, onYes, onNo }) {
  return (<div className="ppv2-selbar show" role="toolbar"><span className="n tab">Delete {n} check{n > 1 ? "s" : ""}?</span>
    <button className="sb main" onClick={onYes}>Delete</button><button className="sb" onClick={onNo}>Keep</button></div>);
}

function RowMenu({ menu, onClose, navigate, f, state, onMark, onDl, onDelete }) {
  const ref = useRef(null);
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); }; setTimeout(() => document.addEventListener("mousedown", h), 0); return () => document.removeEventListener("mousedown", h); }, [onClose]);
  if (!f) return null;
  const proc = state === "processing";
  const era = eraStatusOf(f);
  return (
    <div ref={ref} className="ppv2-rowmenu" role="menu" style={{ left: Math.max(8, menu.x), top: menu.y }} data-testid="ppv2-rowmenu">
      <button role="menuitem" onClick={() => { onClose(); navigate(`/payments-v2/${f.file_id}?src=1`); }}>View EOB PDF</button>
      {proc ? null : <>
        <button role="menuitem" onClick={() => { onClose(); navigate(`/payments-v2/${f.file_id}`); }}>Edit mappings…</button>
        {era === "generated" ? <button role="menuitem" onClick={onMark}>Mark as downloaded</button> : null}
        {era === "downloaded" ? <button role="menuitem" onClick={onDl}>Download 835 again</button> : null}
      </>}
      <hr />
      <button role="menuitem" className="danger" onClick={onDelete}>{proc ? "Delete file…" : "Delete check…"}</button>
    </div>
  );
}

function FilterPop({ draft, setDraft, anchor, ledger, bkt, readyMap, onApply, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null); // null until measured — prevents the (0,0) flash
  useLayoutEffect(() => {
    if (anchor) { const b = anchor.getBoundingClientRect(); const w = 420; setPos({ top: b.bottom + 8, left: Math.max(8, Math.min(b.right - w, window.innerWidth - w - 8)) }); }
  }, [anchor]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target) && e.target !== anchor && !anchor?.contains(e.target)) onClose(); };
    const onWin = () => onClose();
    setTimeout(() => document.addEventListener("mousedown", h), 0);
    window.addEventListener("resize", onWin);
    return () => { document.removeEventListener("mousedown", h); window.removeEventListener("resize", onWin); };
  }, [anchor, onClose]);
  const inScope = (f) => inScopeOf(f, draft.scope);
  const scoped = ledger.filter((r) => inScope(r.f));
  const cGaps = scoped.filter((r) => mappingGap(r.f, readyMap, bkt)).length;
  const cDup = scoped.filter((r) => r.copies.length).length;
  const cPartial = scoped.filter((r) => partiallyComplete(r.f)).length;
  const cReview = scoped.filter((r) => r.f.needs_review).length;
  const bys = [...new Set(ledger.map((r) => uploadedBy(r.f)).filter(Boolean))];
  const flag = (on, dis, label, count, onClick) => (<button className={`fc ${on ? "on" : ""}`} disabled={dis} onClick={onClick}>{on ? <Ck /> : null}{label}{count != null ? <span className="tab">{count}</span> : null}</button>);
  const hits = ledger.filter((r) => {
    const f = r.f;
    if (!inScope(f)) return false;
    if (draft.min !== "" && amtOf(f) < +draft.min) return false; if (draft.max !== "" && amtOf(f) > +draft.max) return false;
    if (draft.payer && !norm(payerName(f)).includes(norm(draft.payer))) return false;
    if (draft.by !== "any" && uploadedBy(f) !== draft.by) return false;
    if (draft.gaps && !mappingGap(f, readyMap, bkt)) return false;
    if (draft.copies && !r.copies.length) return false;
    if (draft.partial && !partiallyComplete(f)) return false;
    if (draft.review && !f.needs_review) return false;
    return true;
  }).length;
  return (
    <div ref={ref} className="ppv2-fpop ppv2" role="dialog" aria-label="Filters" style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? "visible" : "hidden" }} data-testid="ppv2-filterpop">
      <div className="fsec f2">
        <div><div className="flbl">Check amount</div><div className="fpair"><input className="inp" placeholder="Min" value={draft.min} onChange={(e) => setDraft({ ...draft, min: e.target.value.replace(/[^\d.]/g, "") })} />–<input className="inp" placeholder="Max" value={draft.max} onChange={(e) => setDraft({ ...draft, max: e.target.value.replace(/[^\d.]/g, "") })} /></div></div>
        <div><div className="flbl">Payer</div><input className="inp" placeholder="Any payer" value={draft.payer} onChange={(e) => setDraft({ ...draft, payer: e.target.value })} /></div>
      </div>
      <div className="fsec"><div className="flbl">Added by</div><select className="inp" value={draft.by} onChange={(e) => setDraft({ ...draft, by: e.target.value })}>
        <option value="any">Anyone</option>{bys.map((u) => <option key={u} value={u}>{u}</option>)}</select></div>
      <div className="fsec"><div className="flbl">Only checks that are</div><div className="fchips">
        {flag(draft.gaps, !cGaps, "Missing a mapping", cGaps, () => setDraft({ ...draft, gaps: !draft.gaps }))}
        {flag(draft.copies, !cDup, "Duplicated (has copies)", cDup, () => setDraft({ ...draft, copies: !draft.copies }))}
        {flag(draft.partial, !cPartial, "Partially complete", cPartial, () => setDraft({ ...draft, partial: !draft.partial }))}
        {flag(draft.review, !cReview, "Needs review", cReview, () => setDraft({ ...draft, review: !draft.review }))}</div></div>
      <div className="fft"><button className="txt" onClick={() => setDraft({ ...draft, min: "", max: "", payer: "", by: "any", gaps: false, copies: false, partial: false, review: false })}>Clear</button><span className="sp" />
        <button className="btn pri" disabled={!hits} onClick={() => onApply(draft)}>{hits ? `Show ${hits} check${hits !== 1 ? "s" : ""}` : "No checks match"}</button></div>
    </div>
  );
}

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}
